/**
 * CloseOutWizard (flows) — 7-step SDB box close-out with jXchange wiring.
 *
 * Port of the legacy SPFx close-out flow (SDB management + compliance
 * procedures in SdbManagementScreen.tsx) re-expressed as the seven
 * logical steps the v3 brief requires:
 *
 *   1. Verify renter         — ID match (voluntary) or drilling auth (drilled)
 *   2. Final access audit    — record last access, inventory contents
 *   3. Drill-out             — locksmith attestation + key condition (drilled only)
 *   4. Disposition           — return to renter / destroy / escheat to MO
 *   5. AcctClose             — jXchange Deposit/AcctClose + accounts.safedepVacate
 *   6. Fee settlement        — key refund/charge + lost-key fee matrix
 *   7. Archive               — Release form v23.1 + audit event + close task
 *
 * Unlike the legacy 10-step `CloseOutWizard.tsx` at the module root (kept
 * in place until routes flip), this flow wires the Core Director close:
 *   - `apiClient.jxrl.acctClose({ InAcctId, CloseReason, OperatorNumber })`
 *   - `apiClient.accounts.safedepVacate({ AcctId, GlProdCode, RentalRateIdx })`
 *     (flips field 38 VACANT and, if keysMissing === true, field 801).
 *
 * NOTHING is hardcoded. Key-replacement fees read from
 * `config.sdbFeeSchedule` (or '—' if unseeded). Abandonment years never
 * appear here — that is the EscheatmentService's responsibility.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, CheckCircle2, AlertTriangle } from 'lucide-react';
import {
  Button,
  Pill,
  Stepper,
  CardRadioGroup,
  KVPair,
  WidgetCard,
} from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { asIso8601 } from '@retail-ops/shared-types';
import { useSdbDetail } from '../hooks/useSdbDetail';
import { EscheatmentService, type EscheatmentHandoffResult } from '../services/EscheatmentService';
import '../rsm.css';

type ClosePath = 'voluntary' | 'drilled';
type Disposition = 'return-to-renter' | 'destroy' | 'escheat';
type KeyCondition = 'both-returned' | 'one-lost' | 'both-lost';

interface WizardState {
  closePath: ClosePath;
  idVerified: boolean;
  drillingAuthBy: string;

  finalAccessWitnessed: boolean;
  inventoryNotes: string;

  locksmithVendor: string;
  drillAttestation: boolean;

  disposition: Disposition;

  closeSubmitted: boolean;
  closeError: string | null;

  keyCondition: KeyCondition;
  keyDepositOnFile: boolean;

  releaseFormQueued: boolean;
  escheatmentResult: EscheatmentHandoffResult | null;
}

const DEFAULT_STATE: WizardState = {
  closePath: 'voluntary',
  idVerified: false,
  drillingAuthBy: '',
  finalAccessWitnessed: false,
  inventoryNotes: '',
  locksmithVendor: '',
  drillAttestation: false,
  disposition: 'return-to-renter',
  closeSubmitted: false,
  closeError: null,
  keyCondition: 'both-returned',
  keyDepositOnFile: false,
  releaseFormQueued: false,
  escheatmentResult: null,
};

const STEPS = [
  'Verify renter',
  'Final access audit',
  'Drill-out',
  'Disposition',
  'AcctClose',
  'Fee settlement',
  'Archive',
] as const;

type FeeCalc = {
  direction: 'refund' | 'charge' | 'none' | 'unseeded';
  amount: number | null;
  lostKeyFeeRate: number | null;
  keyDeposit: number | null;
  lostKeyCount: number;
  source: 'config' | 'box' | 'none';
};

function computeFeeCalc(
  state: WizardState,
  lostKeyFeeRate: number | null,
  keyDepositAmount: number | null,
  source: 'config' | 'box' | 'none',
): FeeCalc {
  const lostKeyCount = state.keyCondition === 'both-lost' ? 2 : state.keyCondition === 'one-lost' ? 1 : 0;
  if (lostKeyFeeRate == null) {
    return {
      direction: 'unseeded',
      amount: null,
      lostKeyFeeRate: null,
      keyDeposit: keyDepositAmount,
      lostKeyCount,
      source,
    };
  }
  const lostFee = lostKeyCount * lostKeyFeeRate;
  if (state.keyDepositOnFile && keyDepositAmount != null && keyDepositAmount > 0) {
    const refund = keyDepositAmount - lostFee;
    if (refund > 0) {
      return { direction: 'refund', amount: refund, lostKeyFeeRate, keyDeposit: keyDepositAmount, lostKeyCount, source };
    }
    if (refund < 0) {
      return {
        direction: 'charge',
        amount: Math.abs(refund),
        lostKeyFeeRate,
        keyDeposit: keyDepositAmount,
        lostKeyCount,
        source,
      };
    }
    return { direction: 'none', amount: 0, lostKeyFeeRate, keyDeposit: keyDepositAmount, lostKeyCount, source };
  }
  if (lostFee > 0) {
    return { direction: 'charge', amount: lostFee, lostKeyFeeRate, keyDeposit: keyDepositAmount, lostKeyCount, source };
  }
  return { direction: 'none', amount: 0, lostKeyFeeRate, keyDeposit: keyDepositAmount, lostKeyCount, source };
}

function currency(n: number | null): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function CloseOutWizard(): JSX.Element {
  const { acctId } = useParams<{ acctId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { user } = useAuth();
  const config = useConfig();

  const { data: box, loading, error: boxError, reload: reloadBox } = useSdbDetail(acctId);

  const [stepIndex, setStepIndex] = useState(0);
  const [state, setState] = useState<WizardState>(DEFAULT_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const update = <K extends keyof WizardState>(k: K, v: WizardState[K]): void => {
    setState((prev) => ({ ...prev, [k]: v }));
  };

  // --- Fee schedule lookup (config-first, then Core AcctInq, then unseeded). ---
  const feeCalc: FeeCalc = useMemo(() => {
    const boxSize = box?.sizeCode?.trim() ?? '';
    const feeRow = (config.config.sdbFeeSchedule ?? []).find(
      (r) => r.boxSize?.trim() === boxSize,
    );
    const configLostKey = feeRow?.keyReplacementFee ? Number.parseFloat(feeRow.keyReplacementFee) : NaN;
    const lostKeyFeeRate = Number.isFinite(configLostKey) ? configLostKey : null;
    const keyDepositAmount = box?.keyDeposit ?? null;
    return computeFeeCalc(
      state,
      lostKeyFeeRate,
      keyDepositAmount,
      lostKeyFeeRate != null ? 'config' : keyDepositAmount != null ? 'box' : 'none',
    );
  }, [state, config.config.sdbFeeSchedule, box?.sizeCode, box?.keyDeposit]);

  // --- Step gating. ---
  const stepOk = (i: number): boolean => {
    switch (i) {
      case 0:
        return state.closePath === 'voluntary'
          ? state.idVerified
          : state.drillingAuthBy.trim().length > 0;
      case 1:
        return state.finalAccessWitnessed && state.inventoryNotes.trim().length > 0;
      case 2:
        return state.closePath === 'voluntary'
          ? true
          : state.locksmithVendor.trim().length > 0 && state.drillAttestation;
      case 3:
        return true;
      case 4:
        return state.closeSubmitted;
      case 5:
        return true;
      case 6:
        return state.releaseFormQueued;
      default:
        return true;
    }
  };

  const next = (): void => {
    if (stepIndex >= STEPS.length - 1) return;
    let target = stepIndex + 1;
    // Skip drill-out on voluntary path.
    if (state.closePath === 'voluntary' && STEPS[target] === 'Drill-out') target += 1;
    setStepIndex(Math.min(target, STEPS.length - 1));
  };
  const prev = (): void => {
    if (stepIndex === 0) return;
    let target = stepIndex - 1;
    if (state.closePath === 'voluntary' && STEPS[target] === 'Drill-out') target -= 1;
    setStepIndex(Math.max(target, 0));
  };

  // --- Core Director close: Deposit/AcctClose + safedepVacate. ---
  const submitCoreClose = async (): Promise<void> => {
    if (!user || !acctId || !box) return;
    setSubmitting(true);
    setError(null);
    try {
      const closeReason =
        state.closePath === 'voluntary'
          ? 'VoluntaryCustomerClose'
          : state.disposition === 'escheat'
          ? 'DrilledEscheat'
          : state.disposition === 'destroy'
          ? 'DrilledDestroy'
          : 'DrilledReturn';

      await apiClient.jxrl.acctClose({
        InAcctId: { AcctId: acctId, AcctType: '50' },
        CloseReason: closeReason,
        KeysMissing: state.keyCondition !== 'both-returned',
        OperatorNumber: user.officerCode ?? undefined,
      });

      // Vacate the physical box (field 38 → VACANT; field 801 if keys missing).
      await apiClient.accounts.safedepVacate({
        AcctId: acctId,
        GlProdCode: box.productCode ?? '',
        RentalRateIdx: box.sizeCode ?? '',
        OperatorNumber: user.officerCode ?? '',
      } as unknown as Parameters<typeof apiClient.accounts.safedepVacate>[0]);

      // P1 fix 2026-04-27: emit SDB_LEASE close-out disclosure event so the
      // Wave-A backend sweep can compile + deliver the lease close-out copy
      // (auto-debit vs bill-by-notice variant resolved server-side from the
      // box's billing method). Wave 12 just real-bodied the SDB_LEASE
      // template; this is the missing invoke at the close-out moment.
      await apiClient.retail.events
        .write({
          eventType: 'disclosure-redelivery-required',
          eventCategory: 'compliance',
          severity: 'info',
          actor: user.upn ?? 'unknown',
          payloadJson: JSON.stringify({
            action: 'sdb-close-out',
            cifId: box.primaryCifId,
            accountNumber: acctId,
            templates: ['SDB_LEASE'],
            triggerReg: 'PCB_SDB_LEASE_CLOSE_OUT',
            actionPayload: {
              boxNumber: box.boxNumber,
              closeReason,
              disposition: state.disposition,
            },
            idempotencyKey: `sdb-close-out|${acctId}|SDB_LEASE`,
          }),
        })
        .catch(() => undefined);

      update('closeSubmitted', true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Core Director close failed.';
      setError(msg);
      update('closeError', msg);
    } finally {
      setSubmitting(false);
    }
  };

  // --- Escheatment handoff (drilled + disposition=escheat only). ---
  const submitEscheatment = async (): Promise<void> => {
    if (!user || !acctId || !box) return;
    setSubmitting(true);
    setError(null);
    try {
      const svc = new EscheatmentService(apiClient);
      const result = await svc.queueHandoff({
        acctId,
        boxNumber: box.boxNumber,
        branchId: box.branchId,
        escheatType: 'drilled',
        primaryCifId: box.primaryCifId,
        primaryCustomerName: box.primaryCustomerName,
        triggerEventDate: new Date().toISOString().slice(0, 10),
        contentsDescription: state.inventoryNotes,
        estimatedValue: null,
        dueDiligenceSent: state.closePath === 'drilled',
        authorizedBy: user.upn,
      });
      update('escheatmentResult', result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Escheatment handoff failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Archive + release form. ---
  const archive = async (): Promise<void> => {
    if (!user || !acctId || !box) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.retail.events.write({
        eventType: state.closePath === 'voluntary' ? 'sdb-closed-voluntary' : 'sdb-closed-drilled',
        eventCategory: 'compliance',
        severity: 'warn',
        actor: user.upn,
        payloadJson: JSON.stringify({
          acctId,
          boxNumber: box.boxNumber,
          branchId: box.branchId,
          closePath: state.closePath,
          disposition: state.disposition,
          keyCondition: state.keyCondition,
          keyDepositOnFile: state.keyDepositOnFile,
          lostKeyCount: feeCalc.lostKeyCount,
          feeDirection: feeCalc.direction,
          feeAmount: feeCalc.amount,
          feeSource: feeCalc.source,
          drillingAuthBy: state.drillingAuthBy || null,
          locksmithVendor: state.locksmithVendor || null,
          escheatmentEventId: state.escheatmentResult?.eventId ?? null,
          escheatmentTaskId: state.escheatmentResult?.taskId ?? null,
        }),
        timestamp: asIso8601(new Date().toISOString()),
      });
      update('releaseFormQueued', true);
      setDone(true);
      reloadBox();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Archive step failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="rsm-detail">
        <p>Loading box {acctId}…</p>
      </div>
    );
  }
  if (boxError || !box) {
    return (
      <div className="rsm-detail">
        <div className="rsm-banner rsm-banner--err" role="alert">
          {boxError ?? 'Box not found.'}
        </div>
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back
        </Button>
      </div>
    );
  }

  const stepLabels = STEPS.map((l) => ({
    label: l,
    ...(state.closePath === 'voluntary' && l === 'Drill-out' ? { skipLabel: 'N/A' } : {}),
  }));

  const renderStep = (): JSX.Element => {
    switch (stepIndex) {
      case 0:
        return (
          <div className="rsm-wizard__step">
            <div className="rsm-wizard__step-head">
              <h3>Step 1 · Verify renter</h3>
            </div>
            <CardRadioGroup<ClosePath>
              name="close-path"
              selected={state.closePath}
              onChange={(v) => update('closePath', v)}
              options={[
                {
                  value: 'voluntary',
                  primary: 'Voluntary close-out',
                  helper: 'Customer present. Verify government ID against CIF.',
                },
                {
                  value: 'drilled',
                  primary: 'Drilled (past-due / deceased)',
                  helper: 'Supervisor-authorized. Record supervisor UPN for audit.',
                },
              ]}
            />
            {state.closePath === 'voluntary' ? (
              <label className="rsm-check-row rsm-check-row--spaced">
                <input
                  type="checkbox"
                  checked={state.idVerified}
                  onChange={(e) => update('idVerified', e.target.checked)}
                />
                Government ID verified and matches CIF record.
              </label>
            ) : (
              <div className="rsm-field rsm-field--space">
                <label htmlFor="co-drillauth">Retail Supervisor UPN (authorizing drilling)</label>
                <input
                  id="co-drillauth"
                  type="text"
                  value={state.drillingAuthBy}
                  onChange={(e) => update('drillingAuthBy', e.target.value)}
                  placeholder="supervisor@pcb.bank"
                />
              </div>
            )}
          </div>
        );
      case 1:
        return (
          <div className="rsm-wizard__step">
            <div className="rsm-wizard__step-head">
              <h3>Step 2 · Final access audit</h3>
            </div>
            <div className="rsm-detail__kvgrid">
              <KVPair label="Box" value={box.boxNumber ?? box.acctId} />
              <KVPair label="Last access" value={box.lastAccessDate ?? '—'} />
              <KVPair
                label="Lifetime accesses"
                value={box.accessCount == null ? '—' : String(box.accessCount)}
              />
            </div>
            <label className="rsm-check-row rsm-check-row--close">
              <input
                type="checkbox"
                checked={state.finalAccessWitnessed}
                onChange={(e) => update('finalAccessWitnessed', e.target.checked)}
              />
              Final access witnessed by a second PCB employee.
            </label>
            <div className="rsm-field">
              <label htmlFor="co-inv">Inventory notes</label>
              <textarea
                id="co-inv"
                value={state.inventoryNotes}
                onChange={(e) => update('inventoryNotes', e.target.value)}
                placeholder="List of contents removed / inventoried."
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="rsm-wizard__step">
            <div className="rsm-wizard__step-head">
              <h3>Step 3 · Drill-out</h3>
              {state.closePath === 'voluntary' && <Pill variant="muted">Skipped</Pill>}
            </div>
            {state.closePath === 'voluntary' ? (
              <p className="rsm-muted">Skipped on voluntary close-out.</p>
            ) : (
              <>
                <div className="rsm-field">
                  <label htmlFor="co-locksmith">Locksmith vendor</label>
                  <input
                    id="co-locksmith"
                    type="text"
                    value={state.locksmithVendor}
                    onChange={(e) => update('locksmithVendor', e.target.value)}
                    placeholder="Vendor name / invoice #"
                  />
                </div>
                <label className="rsm-check-row rsm-check-row--close">
                  <input
                    type="checkbox"
                    checked={state.drillAttestation}
                    onChange={(e) => update('drillAttestation', e.target.checked)}
                  />
                  Drilling attested by RSM supervisor + witness (signatures collected on paper Record of Contents).
                </label>
              </>
            )}
          </div>
        );
      case 3:
        return (
          <div className="rsm-wizard__step">
            <div className="rsm-wizard__step-head">
              <h3>Step 4 · Disposition</h3>
            </div>
            <CardRadioGroup<Disposition>
              name="disposition"
              selected={state.disposition}
              onChange={(v) => update('disposition', v)}
              options={[
                {
                  value: 'return-to-renter',
                  primary: 'Return contents to renter / estate',
                  helper: 'Voluntary close-out or drilled box with estate claim present.',
                },
                {
                  value: 'destroy',
                  primary: 'Destroy (no claimable contents)',
                  helper: 'Contents inventoried and destroyed per bank procedure.',
                },
                {
                  value: 'escheat',
                  primary: 'Escheat to MO State Treasurer',
                  helper: 'Drilled / deceased with unclaimed contents. Queues RCC handoff.',
                },
              ]}
            />
            {state.disposition === 'escheat' && (
              <div className="rsm-banner rsm-banner--info rsm-field--space">
                <AlertTriangle size={14} className="rsm-inline-icon" />
                <div>
                  Escheatment queued in step 7 (Archive). Abandonment period + report deadline are
                  sourced from <code>/v3/config/escheatment-rules</code> — no hardcoded MO years.
                </div>
              </div>
            )}
          </div>
        );
      case 4:
        return (
          <div className="rsm-wizard__step">
            <div className="rsm-wizard__step-head">
              <h3>Step 5 · AcctClose (jXchange)</h3>
              {state.closeSubmitted && <Pill variant="ok">Submitted</Pill>}
            </div>
            <div className="rsm-detail__kvgrid">
              <KVPair label="Acct type" value="50 (Safe Deposit)" />
              <KVPair label="Close reason" value={state.closePath === 'voluntary' ? 'VoluntaryCustomerClose' : `Drilled${state.disposition}`} />
              <KVPair
                label="Keys missing"
                value={state.keyCondition === 'both-returned' ? 'No' : state.keyCondition === 'one-lost' ? 'Yes (1)' : 'Yes (2)'}
              />
              <KVPair label="Operator" value={user?.officerCode ?? user?.upn ?? '—'} />
            </div>
            {!state.closeSubmitted ? (
              <div className="rsm-action-btn">
                <Button
                  variant="primary"
                  onClick={() => void submitCoreClose()}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting to Core Director…' : 'Submit Deposit/AcctClose + safedep-vacate'}
                </Button>
              </div>
            ) : (
              <div className="rsm-banner rsm-banner--ok rsm-action-followup" role="status">
                <CheckCircle2 size={16} className="rsm-inline-icon" />
                <div>Core Director closed the account and flipped field 38 VACANT.</div>
              </div>
            )}
            {state.closeError && (
              <div className="rsm-banner rsm-banner--err rsm-action-followup" role="alert">
                {state.closeError}
              </div>
            )}
          </div>
        );
      case 5:
        return (
          <div className="rsm-wizard__step">
            <div className="rsm-wizard__step-head">
              <h3>Step 6 · Fee settlement</h3>
              {feeCalc.source === 'none' && <Pill variant="muted">Fee schedule unseeded</Pill>}
            </div>
            <div className="rsm-toolbar">
              <div className="rsm-field">
                <label htmlFor="co-keys">Key condition</label>
                <select
                  id="co-keys"
                  value={state.keyCondition}
                  onChange={(e) => update('keyCondition', e.target.value as KeyCondition)}
                >
                  <option value="both-returned">Both returned</option>
                  <option value="one-lost">One lost</option>
                  <option value="both-lost">Both lost</option>
                </select>
              </div>
              <div className="rsm-field">
                <label htmlFor="co-deposit">Key deposit on file (field 800)?</label>
                <select
                  id="co-deposit"
                  value={state.keyDepositOnFile ? 'yes' : 'no'}
                  onChange={(e) => update('keyDepositOnFile', e.target.value === 'yes')}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
            </div>
            <WidgetCard title="Calculated fee" stripe="approvals">
              <div className="rsm-detail__kvgrid">
                <KVPair
                  label="Lost-key rate (config)"
                  value={feeCalc.lostKeyFeeRate == null ? '—' : currency(feeCalc.lostKeyFeeRate)}
                />
                <KVPair
                  label="Key deposit (field 800)"
                  value={feeCalc.keyDeposit == null ? '—' : currency(feeCalc.keyDeposit)}
                />
                <KVPair label="Lost key count" value={String(feeCalc.lostKeyCount)} />
                <KVPair
                  label="Direction"
                  value={
                    feeCalc.direction === 'unseeded'
                      ? 'Unseeded — seed sdbFeeSchedule to compute'
                      : feeCalc.direction === 'refund'
                      ? 'Refund to customer'
                      : feeCalc.direction === 'charge'
                      ? 'Charge to customer'
                      : 'None'
                  }
                />
                <KVPair label="Amount" value={feeCalc.amount == null ? '—' : currency(feeCalc.amount)} />
                <KVPair label="Source" value={feeCalc.source} />
              </div>
            </WidgetCard>

            {state.disposition === 'escheat' && !state.escheatmentResult && (
              <div className="rsm-action-btn">
                <Button
                  variant="primary"
                  onClick={() => void submitEscheatment()}
                  disabled={submitting}
                >
                  {submitting ? 'Queueing escheatment…' : 'Queue MO escheatment handoff'}
                </Button>
              </div>
            )}
            {state.escheatmentResult && (
              <div className="rsm-banner rsm-banner--ok rsm-action-followup" role="status">
                <CheckCircle2 size={16} className="rsm-inline-icon" />
                <div>
                  Escheatment queued. Report due{' '}
                  {state.escheatmentResult.reportDueDate ?? '—'} (abandonment{' '}
                  {state.escheatmentResult.abandonmentYears == null
                    ? '—'
                    : `${state.escheatmentResult.abandonmentYears}y`}
                  ).
                </div>
              </div>
            )}
          </div>
        );
      case 6:
        return (
          <div className="rsm-wizard__step">
            <div className="rsm-wizard__step-head">
              <h3>Step 7 · Archive</h3>
            </div>
            <p className="rsm-muted">
              Writing the audit event and queueing Release form v23.1 via RNC.
            </p>
            {!state.releaseFormQueued ? (
              <Button
                variant="green"
                onClick={() => void archive()}
                disabled={submitting || !state.closeSubmitted}
              >
                {submitting ? 'Archiving…' : 'Archive + send release form'}
              </Button>
            ) : (
              <div className="rsm-banner rsm-banner--ok" role="status">
                <CheckCircle2 size={16} className="rsm-inline-icon" />
                <div>Audit event written. Release form queued for RNC delivery.</div>
              </div>
            )}
          </div>
        );
      default:
        return <div>Unknown step.</div>;
    }
  };

  const isLast = stepIndex === STEPS.length - 1;
  const canAdvance = stepOk(stepIndex);

  if (done) {
    return (
      <div className="rsm-wizard">
        <div className="rsm-banner rsm-banner--ok" role="status">
          <CheckCircle2 size={16} className="rsm-inline-icon" />
          <div>
            Close-out complete. Box {box.boxNumber ?? box.acctId} is VACANT; audit trail archived.
            {state.escheatmentResult && ' Escheatment queued for RCC.'}
          </div>
        </div>
        <div className="rsm-wizard__footer">
          <Button variant="ghost" onClick={() => navigate('../..')}>
            Back to RSM
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rsm-wizard">
      <div className="rsm-detail__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back to box
        </Button>
        <h1>Close-out · Box {box.boxNumber ?? box.acctId}</h1>
        <Pill variant={state.closePath === 'drilled' ? 'warn' : 'info'}>
          {state.closePath === 'drilled' ? 'Drilled' : 'Voluntary'}
        </Pill>
      </div>

      <Stepper steps={stepLabels} currentIndex={stepIndex} />

      {renderStep()}

      {error && (
        <div className="rsm-banner rsm-banner--err" role="alert">
          {error}
        </div>
      )}

      <div className="rsm-wizard__footer">
        <Button variant="ghost" onClick={prev} disabled={stepIndex === 0}>
          Previous
        </Button>
        {!isLast ? (
          <Button variant="primary" onClick={next} disabled={!canAdvance}>
            Next
          </Button>
        ) : (
          <Button variant="ghost" onClick={() => navigate('../..')} disabled={!canAdvance}>
            Finish
          </Button>
        )}
      </div>
    </div>
  );
}
